import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EyeOff, Megaphone, Sparkles } from "lucide-react";
import { api } from "@/api/client";
import type { BlindOverviewData, DashboardBrandSeries } from "../../../types";
import { useModelsStore } from "@/stores/models";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { NativeSelect } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { cn } from "@/lib/utils";

const WINDOWS = [
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 180, label: "Last 180 days" },
];

function fmtSqlDate(sql: string): string {
  return new Date(sql.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function BlindView() {
  const catalog = useModelsStore((s) => s.models);
  const fetchModels = useModelsStore((s) => s.fetch);

  const [data, setData] = useState<BlindOverviewData | null>(null);
  const [days, setDays] = useState(90);
  const [model, setModel] = useState("");
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { fetchModels(); }, []);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.metrics.blindOverview({ days, model: model || undefined })
      .then((d) => { if (!cancelled) { setData(d); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load blind query data"); });
    return () => { cancelled = true; };
  }, [days, model, reloadKey]);

  if (error) return <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!data) return <Spinner message="Loading blind query data…" size="lg" />;

  if (!data.has_data) {
    return (
      <EmptyState
        icon={EyeOff}
        title="No blind queries yet"
        description="Blind queries ask AI a neutral question with no brands named, then check who it brings up on its own. Run one, or turn on blind tracking in a topic space."
        action={
          <div className="flex items-center justify-center gap-2">
            <Link to="/app/compare?mode=blind"><Button>Run a blind query</Button></Link>
            <Link to="/app/topics/new"><Button variant="secondary">New topic space</Button></Link>
          </div>
        }
      />
    );
  }

  const sov = data.share_of_voice.filter((e) => e.share > 0).slice(0, 10);
  const topShare = sov.length > 0 ? Math.max(...sov.map((e) => e.share)) : 0;
  // Map SoV trend series into the shared TrendChart shape; untracked names get
  // synthetic negative ids so the chart can key them.
  const trendBrands: DashboardBrandSeries[] = data.sov_trend.map((s, i) => ({
    brand_id: s.brand_id ?? -(i + 1),
    brand_name: s.tracked ? s.brand_name : `${s.brand_name} (untracked)`,
    is_primary: s.brand_id !== null && s.brand_id === data.primary_brand?.brand_id,
    latest: s.points.length > 0 ? s.points[s.points.length - 1]!.share : null,
    deltas: { day: null, week: null, month: null },
    series: s.points.map((p) => ({ bucket: p.bucket, avg: p.share, min: p.share, max: p.share, count: 1 })),
  }));

  return (
    <div className="space-y-5">
      {/* Filters + run CTA */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <NativeSelect value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-auto">
            {WINDOWS.map((w) => <option key={w.days} value={w.days}>{w.label}</option>)}
          </NativeSelect>
          <NativeSelect value={model} onChange={(e) => setModel(e.target.value)} className="w-auto">
            <option value="">All models</option>
            {catalog.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </NativeSelect>
        </div>
        <Link to="/app/compare?mode=blind">
          <Button variant="secondary"><Sparkles className="w-4 h-4 mr-1.5" />Run a blind query now</Button>
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 stagger-children">
        {/* Share of voice */}
        <Card className={cn(data.sov_trend.length === 0 && "lg:col-span-2")}>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-brand-500" />
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Share of voice — who AI surfaces</span>
            </div>
            <div className="space-y-2">
              {sov.map((e) => (
                <div key={e.brand_name} className="grid grid-cols-[minmax(110px,1fr)_2fr_auto] items-center gap-3">
                  <span className={cn("text-[13px] truncate flex items-center gap-1.5", e.tracked ? "font-semibold text-brand-950" : "text-slate-600")}>
                    {e.brand_name}
                    {!e.tracked && <Badge variant="muted">untracked</Badge>}
                  </span>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-brand-500" style={{ width: `${topShare > 0 ? (e.share / topShare) * 100 : 0}%` }} />
                  </div>
                  <span className="text-[12.5px] font-semibold tabular-nums text-slate-600 text-right">{e.share.toFixed(0)}%</span>
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-slate-400">
              Visibility-weighted share across every blind answer in the window — the question never names your brands.
            </p>
          </CardContent>
        </Card>

        {/* SoV trend */}
        {data.sov_trend.length > 0 && (
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Share of voice over time</span>
                <span className="text-[11px] text-slate-400">Top {data.sov_trend.length} names · {data.bucket === "week" ? "weekly" : "daily"}</span>
              </div>
              <TrendChart brands={trendBrands} bucket={data.bucket} heroBrandId={data.primary_brand?.brand_id ?? null} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Per-topic presence */}
      {data.topics.length > 0 && (
        <Card>
          <CardContent className="space-y-1">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">By topic — who owns each answer</span>
            {data.topics.map((t) => (
              <div key={t.category} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <Badge variant="category">{t.category}</Badge>
                <span className="text-[12.5px] text-slate-500 tabular-nums shrink-0">{t.runs} run{t.runs === 1 ? "" : "s"}</span>
                <span className="text-[12.5px] text-slate-600 truncate">
                  {t.top_brands.map((b, i) => (
                    <span key={b.brand_name}>
                      {i > 0 && " · "}
                      <span className={cn(b.tracked ? "font-semibold text-brand-950" : "")}>{b.brand_name}</span>
                      <span className="text-slate-400"> {b.share.toFixed(0)}%</span>
                    </span>
                  ))}
                </span>
                <span className="ml-auto text-[12px] tabular-nums shrink-0">
                  {t.primary_share !== null
                    ? <span className="font-semibold text-brand-700">you: {t.primary_share.toFixed(0)}%</span>
                    : <span className="text-red-500 font-medium">you: absent</span>}
                </span>
                <Link to={`/app/topics/${t.topic_id ?? encodeURIComponent(t.category)}`} className="text-[12px] font-medium text-brand-600 hover:underline shrink-0">
                  Drill in →
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2 stagger-children">
        {/* New names */}
        {data.new_brands.length > 0 && (
          <Card>
            <CardContent className="space-y-1">
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">New names appearing in answers</span>
              {data.new_brands.slice(0, 8).map((n) => (
                <div key={n.brand_name} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <span className="text-[13px] font-medium text-slate-700 truncate">{n.brand_name}</span>
                  <span className="text-[12px] text-slate-400 tabular-nums shrink-0">first seen {n.first_seen.slice(0, 10)}</span>
                  <span className="ml-auto text-[12px] text-slate-500 tabular-nums shrink-0">
                    {n.appearances} mention{n.appearances === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
              <p className="text-[11.5px] text-slate-400 pt-1">Untracked names the models started surfacing inside the window.</p>
            </CardContent>
          </Card>
        )}

        {/* Recent runs */}
        {data.recent_runs.length > 0 && (
          <Card className={cn(data.new_brands.length === 0 && "lg:col-span-2")}>
            <CardContent className="space-y-1">
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Recent blind runs</span>
              {data.recent_runs.slice(0, 8).map((r) => (
                <Link
                  key={r.id}
                  to={`/app/compare/results/${r.id}`}
                  className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group"
                >
                  <Badge variant="category">{r.topic}</Badge>
                  <span className="text-[12.5px] text-slate-600 truncate group-hover:text-brand-600">{r.prompt_name}</span>
                  <span className="ml-auto text-[11.5px] text-slate-400 tabular-nums shrink-0">
                    {r.tracked_surfaced} tracked · {r.untracked_count} other{r.untracked_count === 1 ? "" : "s"} · {fmtSqlDate(r.created_at)}
                  </span>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
